import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import { useModelDoOver } from '@hooks/useModelDoOver';
import Image from 'next/image';
import { api } from '@/trpc/react';
import { ModelSelector } from '@components/Model/ModelSelector';

type SelectableModel = {
  status: string;
  id: string;
  name: string;
  trainingMeta: PrismaJson.TrainingMeta | null;
  readableName: string | null;
  selfieUrl: string | null;
  attributes: PrismaJson.ModelAttributes | null;
  modelRepositoryId: string;
  modelRepositoryName: string;
  teams?: Array<{ id: string; name: string }>;
};

interface DoOverDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  defaultModelRepositoryId?: string;
  dialogMode?: 'team' | 'personal';
  contextualTeamId?: string;
  hideDismissButton?: boolean;
}

export function DoOverDialog({
  isOpen,
  onOpenChange,
  defaultModelRepositoryId,
  dialogMode,
  contextualTeamId,
  hideDismissButton
}: DoOverDialogProps) {
  const [selectedModelFromSelector, setSelectedModelFromSelector] = React.useState<SelectableModel | null>(null);

  // Query for the specific model repository if defaultModelRepositoryId is provided
  const targetModelRepositoryQuery = api.modelRepository.get.useQuery(
    { id: defaultModelRepositoryId! },
    { enabled: isOpen && !!defaultModelRepositoryId }
  );

  // Fetch contextual team details if in team mode and teamId is provided
  const { data: contextualTeamData, isLoading: isLoadingContextualTeam } = api.team.get.useQuery(
    { teamId: contextualTeamId! },
    { enabled: isOpen && dialogMode === 'team' && !!contextualTeamId }
  );

  // Query for the list of models based on dialogMode and contextualTeamId
  const userModelsListQuery = api.model.getUserModels.useInfiniteQuery(
    {
      includeTrainingMeta: true,
      // Conditionally pass teamId if dialogMode is 'team' and contextualTeamId is available
      teamId: dialogMode === 'team' && contextualTeamId ? contextualTeamId : undefined
    },
    {
      getNextPageParam: lastPage => lastPage.nextCursor,
      // Enable only if dialog is open AND dialogMode is determined (meaning credit context is known)
      enabled: isOpen && !!dialogMode
    }
  );

  const doOverKit = useModelDoOver({ teamId: contextualTeamId });

  React.useEffect(() => {
    if (!isOpen || !dialogMode) {
      // Also check for dialogMode before proceeding
      setSelectedModelFromSelector(null);
      return;
    }

    let modelSet = false;

    // Attempt to select model based on defaultModelRepositoryId
    if (defaultModelRepositoryId && userModelsListQuery.isSuccess && userModelsListQuery.data) {
      let foundModelInList: SelectableModel | null = null;
      for (const page of userModelsListQuery.data.pages) {
        const model = page.models.find(m => m.modelRepositoryId === defaultModelRepositoryId);
        if (model) {
          foundModelInList = model;
          break;
        }
      }
      if (foundModelInList) {
        if (!selectedModelFromSelector || selectedModelFromSelector.id !== foundModelInList.id) {
          setSelectedModelFromSelector(foundModelInList);
        }
        modelSet = true;
      }
    }

    // Fallback: If no model was set via defaultModelRepositoryId (or no ID was provided)
    if (!modelSet) {
      if (userModelsListQuery.isSuccess && userModelsListQuery.data?.pages?.[0]?.models?.length) {
        const firstModelFromList = userModelsListQuery.data.pages[0].models[0];
        if (firstModelFromList) {
          if (!selectedModelFromSelector || selectedModelFromSelector.id !== firstModelFromList.id) {
            setSelectedModelFromSelector(firstModelFromList);
          }
          modelSet = true;
        }
      }
    }

    if (!modelSet && !targetModelRepositoryQuery.isLoading && !userModelsListQuery.isLoading) {
      if (selectedModelFromSelector !== null) {
        setSelectedModelFromSelector(null);
      }
    }
  }, [
    isOpen,
    dialogMode,
    contextualTeamId,
    defaultModelRepositoryId,
    targetModelRepositoryQuery.isSuccess,
    targetModelRepositoryQuery.isLoading,
    userModelsListQuery.isSuccess,
    userModelsListQuery.data,
    userModelsListQuery.isLoading,
    selectedModelFromSelector
  ]);

  const handleStartDoOverClick = () => {
    if (doOverKit && selectedModelFromSelector) {
      doOverKit.handleDoOver(
        {
          id: selectedModelFromSelector.id!,
          trainingMeta: selectedModelFromSelector.trainingMeta,
          attributes: selectedModelFromSelector.attributes!,
          selfieUrl: selectedModelFromSelector.selfieUrl!
        },
        selectedModelFromSelector.modelRepositoryName
      );
      onOpenChange(false);
    }
  };

  const isStartButtonDisabled = !selectedModelFromSelector;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" closeClassName={hideDismissButton ? 'hidden' : ''}>
        <DialogHeader className="items-center">
          <div className="mb-4">
            <Image
              src="https://storage.prompt-hunt.workers.dev/cm742zrf8000lhiod8aok3qo1_4"
              alt="Do-Over Card"
              width={1024}
              height={1024}
              className="w-32 h-32 rounded-lg mx-auto sm:mx-0"
            />
          </div>
          <DialogTitle className="text-xl font-semibold">
            {dialogMode === 'team' && contextualTeamData && !isLoadingContextualTeam
              ? `Start your Do-Over for ${contextualTeamData.name}`
              : 'Start your Do-Over'}
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm text-muted-foreground">
            Want to improve your model's results? The Do-Over feature lets you retrain your model with a fresh set of
            photos.
          </DialogDescription>
        </DialogHeader>

        <ModelSelector
          selectedModel={selectedModelFromSelector!}
          teamId={contextualTeamId}
          modelRepository={{
            id: selectedModelFromSelector?.modelRepositoryId,
            name: selectedModelFromSelector?.modelRepositoryName
          }}
          onModelChange={model => {
            setSelectedModelFromSelector({
              ...model.model,
              modelRepositoryId: model.modelRepository.id,
              modelRepositoryName: model.modelRepository.name
            });
          }}
          className="!w-full "
        />

        <DialogFooter className="gap-2 sm:gap-0">
          {!hideDismissButton && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Not now
            </Button>
          )}
          <Button onClick={handleStartDoOverClick} disabled={isStartButtonDisabled}>
            Start Do-Over
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
